import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import {
    Box,
    Grid,
    IconButton,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { fetchVisitorBookings } from '../../action/bookingsCommands';
import ReloadIcon from '../../components/icons/ReloadIcon';
import { theme } from '../../material-ui/theme';
import { makeBookingDetailsUrl, paths } from '../../routes';
import { makeVisitorBookingSelector } from '../../selector/bookingsSelector';
import VisitorBookingCard from './VisitorBookingCard';
import VisitorBookingSearchBar from './VisitorBookingSearchBar';
import PageHeader from '../../components/PageHeader';

const VisitorBookingsPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isSomewhatLargeScreen = useMediaQuery(theme.breakpoints.up('sm'));
    const [searchTerm, setSearchTerm] = useState<string>('');

    const visitorBookings = useSelector(makeVisitorBookingSelector());

    useEffect(() => {
        dispatch(fetchVisitorBookings({}));
    }, []);

    const navigateToVisitorBooking = (bookingNumber: string) => {
        navigate(makeBookingDetailsUrl(String(bookingNumber)));
    };

    const navigateToAddIssueReport = () => {
        navigate(paths.addVisitorBooking);
    };

    const reloadVisitorBookings = () => {
        dispatch(fetchVisitorBookings({}));
    };

    const filteredVisitorBookings = visitorBookings.filter((visitorBooking) => {
        const preparedSearchTerm = searchTerm.toLowerCase();
        return visitorBooking.bookingNumber
            .toLowerCase()
            .includes(preparedSearchTerm);
    });

    const visitorBookingList = filteredVisitorBookings.sort(
        (visitorBooking1, visitorBooking2) => {
            if (visitorBooking1.bookingNumber < visitorBooking2.bookingNumber)
                return -1;
            if (visitorBooking1.bookingNumber > visitorBooking2.bookingNumber)
                return 1;
            return 0;
        },
    );

    const visitorBookingsCount = visitorBookingList.length;

    return (
        <Grid container={true} spacing={2}>
            <Grid item={true} xs={12} style={{ paddingTop: '8px' }}>
                <PageHeader>
                    <Typography
                        variant={'body1'}
                        className={'text-2xl text-foreground'}
                    >
                        {visitorBookingsCount}{' '}
                        {t('visitor_booking.visitor_booking_other')}
                    </Typography>
                    <Box>
                        {isSomewhatLargeScreen && (
                            <VisitorBookingSearchBar
                                value={searchTerm}
                                onChange={setSearchTerm}
                            />
                        )}
                        <Tooltip title={'Reload'}>
                            <IconButton
                                className={'text-foreground -mr-2'}
                                onClick={reloadVisitorBookings}
                            >
                                <ReloadIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={'Add'}>
                            <IconButton
                                sx={{ paddingRight: 0 }}
                                onClick={reloadVisitorBookings}
                                size={'large'}
                            >
                                <NoteAddOutlinedIcon
                                    className={'text-foreground'}
                                    onClick={navigateToAddIssueReport}
                                />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </PageHeader>
                <Box>
                    {!isSomewhatLargeScreen && (
                        <VisitorBookingSearchBar
                            value={searchTerm}
                            onChange={setSearchTerm}
                            fullWidth={true}
                        />
                    )}
                </Box>
            </Grid>
            {visitorBookingList.map((visitorBooking) => (
                <Grid
                    item={true}
                    xs={12}
                    sm={6}
                    md={4}
                    xl={3}
                    key={visitorBooking.id}
                >
                    <VisitorBookingCard
                        onPress={() =>
                            navigateToVisitorBooking(
                                visitorBooking.bookingNumber,
                            )
                        }
                        visitorBooking={visitorBooking}
                    />
                </Grid>
            ))}
        </Grid>
    );
};

export default VisitorBookingsPage;
